import { useEffect, useState } from 'react';
import { Button, Checkbox, Col, Divider, Modal, Row, Tooltip, Typography, message } from 'antd';
import {
  CheckCircleFilled,
  CopyOutlined,
  MailOutlined,
  MessageOutlined,
  PrinterOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { usePrint } from '@/hooks/useHardware';
import { useStoreSettings } from '@/hooks/useStoreSettings';
import { useAuth } from '@/hooks/useAuth';
import { formatVND } from '@/utils/format';
import { buildReceiptData, type CheckoutSuccessResult } from '../utils/buildReceiptData';

const { Text, Title } = Typography;

interface CheckoutSuccessDialogProps {
  open: boolean;
  result: CheckoutSuccessResult | null;
  onContinue: () => void;
}

const PENDING_BE = 'Chưa khả dụng — cần kết nối backend';

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

const boxStyle: React.CSSProperties = {
  border: '1px solid #d9d9d9',
  borderRadius: 8,
  padding: 16,
  height: '100%',
};
const rowStyle: React.CSSProperties = { marginBottom: 8 };

export function CheckoutSuccessDialog({ open, result, onContinue }: CheckoutSuccessDialogProps) {
  const print = usePrint();
  const { data: storeSettings } = useStoreSettings();
  const { user } = useAuth();
  const [escpos, setEscpos] = useState(true);

  // Sync default checkbox theo cấu hình autoPrint khi mở dialog.
  useEffect(() => {
    if (open) setEscpos(storeSettings?.autoPrint ?? true);
  }, [open, storeSettings?.autoPrint]);

  if (!result) return null;

  const showCash = result.paymentMethod === 'cash' || (result.cashReceived ?? 0) > 0;

  const handleCopy = () => {
    void navigator.clipboard?.writeText(result.orderCode);
    void message.success('Đã sao chép mã đơn');
  };

  const handlePrint = async () => {
    if (!escpos) {
      void message.info('Chưa chọn định dạng in');
      return;
    }
    try {
      const data = buildReceiptData(result, storeSettings, user?.name ?? '');
      await print.mutateAsync({ type: 'receipt', data, width: 58, openDrawerAfter: true });
      void message.success('Đã gửi lệnh in hoá đơn');
    } catch {
      void message.error('Không in được — kiểm tra máy in + Plugin phần cứng');
    }
  };

  return (
    <Modal open={open} footer={null} closable={false} maskClosable={false} keyboard={false} width={680} centered>
      <Title level={4} style={{ textAlign: 'center', marginBottom: 20 }}>
        <CheckCircleFilled style={{ color: '#52c41a', marginRight: 8 }} />
        ĐƠN HÀNG THÀNH CÔNG
      </Title>

      <Text strong>Thông tin hóa đơn</Text>
      <Row gutter={16} style={{ marginTop: 8, marginBottom: 16 }}>
        <Col xs={24} md={12} style={{ marginBottom: 12 }}>
          <div style={boxStyle}>
            <div style={{ ...rowStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text strong>Mã đơn: #{result.orderCode}</Text>
              <Button size="small" icon={<CopyOutlined />} onClick={handleCopy}>Sao chép</Button>
            </div>
            <div style={rowStyle}><Text>Thời gian: {formatDateTime(result.dateISO)}</Text></div>
            <Divider style={{ margin: '8px 0' }} />
            <div style={rowStyle}><Text strong>Tổng: {formatVND(result.total)}</Text></div>
            {showCash && (
              <>
                <div style={rowStyle}><Text>Tiền mặt: {formatVND(result.cashReceived ?? 0)}</Text></div>
                <div style={rowStyle}><Text>Thối lại: {formatVND(result.change ?? 0)}</Text></div>
              </>
            )}
          </div>
        </Col>
        {result.customerName && (
          <Col xs={24} md={12} style={{ marginBottom: 12 }}>
            <div style={boxStyle}>
              <div style={rowStyle}>
                <UserOutlined style={{ marginRight: 8 }} />
                <Text strong>Khách hàng: {result.customerName}</Text>
              </div>
              <div style={rowStyle}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Diem tich luy se duoc cap nhat
                </Text>
              </div>
            </div>
          </Col>
        )}
      </Row>

      <Text strong><PrinterOutlined style={{ marginRight: 6 }} />In ấn</Text>
      <div style={{ margin: '12px 0', display: 'flex', gap: 24 }}>
        <Checkbox checked={escpos} onChange={(e) => setEscpos(e.target.checked)}>
          Hoá đơn ESC/POS (58mm)
        </Checkbox>
        <Tooltip title={PENDING_BE}>
          <Checkbox disabled>HĐĐT (nếu có)</Checkbox>
        </Tooltip>
      </div>

      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col span={8}>
          <Tooltip title={PENDING_BE}>
            <Button block disabled icon={<MailOutlined />}>Gửi hóa đơn qua Email</Button>
          </Tooltip>
        </Col>
        <Col span={8}>
          <Tooltip title={PENDING_BE}>
            <Button block disabled icon={<MessageOutlined />}>Gửi hóa đơn qua Zalo</Button>
          </Tooltip>
        </Col>
        <Col span={8}>
          <Button block type="primary" icon={<PrinterOutlined />} loading={print.isPending} onClick={() => void handlePrint()}
            style={{ background: '#a4193d', borderColor: '#a4193d' }}>
            In hóa đơn
          </Button>
        </Col>
      </Row>

      <Button block size="large" onClick={onContinue}>Tiếp tục bán hàng</Button>
    </Modal>
  );
}
